import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Check, EyeOff } from "lucide-react"
import { useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"

type Regla = {
  clave: string
  titulo: string
  entidad: string
  campo: string
  pendientes: number
  apartadas: number
  control: string
  ayuda: string | null
  opciones_de: string | null
  ruta_api: string
  prioridad: number
}

type Ficha = { id: number; etiqueta: string; ruta_api: string; campo: string }

type Opcion = { id: number; nombre: string }

/**
 * Pendientes: qué fichas del sistema tienen un dato por completar, y se completan aquí.
 *
 * ⚠ **Esta pantalla no escribe en su propio endpoint.** Cada campo se guarda con el
 * `PATCH` del módulo dueño —el que dice la regla—, así que valida y audita igual que
 * su formulario. Una segunda puerta de escritura se saltaría las dos, y el día que
 * discrepen nadie sabría cuál manda.
 *
 * ⚠ **Ningún pendiente bloquea nada.** El tablero cuenta y ofrece arreglar; el trabajo
 * sigue. Con treinta y dos proveedores sin RIF, bloquear dejaría a la agencia sin
 * poder pagar el alquiler.
 *
 * ⚠ **Lo que se ve está recortado por permiso**, y no por cortesía: quien no puede
 * editar proveedores no ve sus huecos ni recibe un botón que el servidor va a negar.
 */
export function Pendientes() {
  const [abierta, setAbierta] = useState<string | null>(null)

  const reglas = useQuery({
    queryKey: ["pendientes"],
    queryFn: () => api.get<Regla[]>("/api/v1/pendientes"),
  })

  if (reglas.isPending) {
    return <p className="py-6">Cargando…</p>
  }
  if (reglas.isError) {
    return (
      <p
        className="flex items-center gap-2 py-6"
        role="alert"
        style={{ color: "var(--destructive)" }}
      >
        <AlertCircle size={16} /> No se pudo cargar el tablero de pendientes.
      </p>
    )
  }

  const filas = reglas.data
  const total = filas.reduce((suma, r) => suma + r.pendientes, 0)
  const documento = (): DocumentoExportable => ({
    titulo: "Pendientes",
    subtitulo: `Datos por completar en las fichas · ${total} en total`,
    secciones: [
      {
        titulo: "Pendientes por regla",
        columnas: [
          { titulo: "Dato que falta", tipo: "texto" },
          { titulo: "Ficha", tipo: "texto" },
          { titulo: "Pendientes", tipo: "numero" },
          { titulo: "Apartadas", tipo: "numero" },
        ],
        filas: filas.map((r) => ({ celdas: [r.titulo, r.entidad, r.pendientes, r.apartadas] })),
        pie: [["Total", null, total, filas.reduce((suma, r) => suma + r.apartadas, 0)]],
      },
    ],
  })

  return (
    <div>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Pendientes</h1>
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Datos que faltan en fichas que ya existen. Se completan desde aquí, y nada de esto
            impide trabajar.
          </p>
        </div>
        <Exportar titulo="Pendientes" documento={documento} />
      </header>

      {total === 0 ? (
        <p className="tarjeta text-sm">
          No falta ningún dato de los que se vigilan. Cuando se añada un campo nuevo, las fichas
          anteriores aparecerán aquí solas.
        </p>
      ) : (
        <section className="space-y-3">
          {filas.map((regla) => (
            <article key={regla.clave} className="tarjeta">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left"
                onClick={() => setAbierta(abierta === regla.clave ? null : regla.clave)}
                aria-expanded={abierta === regla.clave}
              >
                <span className="font-medium">{regla.titulo}</span>
                <span className="flex items-center gap-2">
                  {regla.apartadas > 0 && (
                    <Insignia tono="neutro">{regla.apartadas} apartadas</Insignia>
                  )}
                  <Insignia tono={regla.pendientes === 0 ? "exito" : "aviso"}>
                    {regla.pendientes}
                  </Insignia>
                </span>
              </button>
              {abierta === regla.clave && regla.pendientes > 0 && <Lista regla={regla} />}
            </article>
          ))}
        </section>
      )}
    </div>
  )
}

function Lista({ regla }: { regla: Regla }) {
  const fichas = useQuery({
    queryKey: ["pendientes", regla.clave],
    queryFn: () => api.get<Ficha[]>(`/api/v1/pendientes/${regla.clave}?limit=100`),
  })
  const opciones = useQuery({
    queryKey: ["opciones", regla.opciones_de],
    queryFn: () => api.get<Opcion[]>(regla.opciones_de ?? ""),
    enabled: regla.control === "select" && regla.opciones_de !== null,
  })

  if (fichas.isPending) return <p className="mt-3 text-sm">Cargando…</p>
  if (fichas.isError) {
    return (
      <p className="mt-3 text-sm" role="alert" style={{ color: "var(--destructive)" }}>
        No se pudieron cargar las fichas.
      </p>
    )
  }

  return (
    <div className="mt-3">
      {regla.ayuda && (
        <p className="mb-2 text-xs" style={{ color: "var(--fg-muted)" }}>
          {regla.ayuda}
        </p>
      )}
      <Tabla etiqueta={regla.titulo}>
        <Cabecera>
          <Th>Ficha</Th>
          <Th>Lo que falta</Th>
          <Th derecha>Acciones</Th>
        </Cabecera>
        <Cuerpo>
          {fichas.data.length === 0 && <TablaVacia colSpan={3}>Nada pendiente aquí.</TablaVacia>}
          {fichas.data.map((ficha) => (
            <FilaPendiente
              key={ficha.id}
              regla={regla}
              ficha={ficha}
              opciones={opciones.data ?? []}
            />
          ))}
        </Cuerpo>
      </Tabla>
    </div>
  )
}

function FilaPendiente({
  regla,
  ficha,
  opciones,
}: {
  regla: Regla
  ficha: Ficha
  opciones: Opcion[]
}) {
  const clienteQuery = useQueryClient()
  const idCampo = useId()
  const [valor, setValor] = useState("")
  const [apartando, setApartando] = useState(false)
  const [motivo, setMotivo] = useState("")

  function refrescar() {
    clienteQuery.invalidateQueries({ queryKey: ["pendientes"] })
    // La ficha cambió: su propio listado también tiene que enterarse.
    clienteQuery.invalidateQueries({ queryKey: [regla.entidad] })
  }

  // ⚠ Llama al PATCH del módulo dueño, nunca a una ruta de este módulo.
  const guardar = useMutation({
    mutationFn: () => api.patch(ficha.ruta_api, { [ficha.campo]: valor.trim() }),
    onSuccess: refrescar,
  })

  const apartar = useMutation({
    mutationFn: () => api.post(`/api/v1/pendientes/${regla.clave}/${ficha.id}/apartar`, { motivo }),
    onSuccess: () => {
      setApartando(false)
      refrescar()
    },
  })

  const error =
    guardar.error instanceof ErrorApi
      ? guardar.error.message
      : apartar.error instanceof ErrorApi
        ? apartar.error.message
        : null

  return (
    <Fila>
      <Td>{ficha.etiqueta}</Td>
      <Td>
        {apartando ? (
          <input
            aria-label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Por qué se queda así"
            className="campo w-full"
          />
        ) : regla.control === "select" ? (
          <select
            id={idCampo}
            aria-label={regla.titulo}
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            className="campo w-full"
          >
            <option value="">Elige una opción…</option>
            {opciones.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.nombre}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={idCampo}
            aria-label={regla.titulo}
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            data-testid={`campo-${regla.clave}-${ficha.id}`}
            className="campo w-full"
          />
        )}
        {error && (
          <span className="mt-1 block text-xs" style={{ color: "var(--destructive)" }}>
            {error}
          </span>
        )}
      </Td>
      <Td derecha>
        <div className="flex justify-end gap-2">
          {apartando ? (
            <>
              <Boton onClick={() => setApartando(false)}>Cancelar</Boton>
              <Boton
                variante="primario"
                disabled={motivo.trim().length < 10 || apartar.isPending}
                onClick={() => apartar.mutate()}
              >
                Apartar
              </Boton>
            </>
          ) : (
            <>
              <Boton
                onClick={() => setApartando(true)}
                title="Esta ficha se queda así, con su motivo escrito"
              >
                <EyeOff size={16} /> No aplica
              </Boton>
              <Boton
                variante="primario"
                disabled={valor.trim() === "" || guardar.isPending}
                onClick={() => guardar.mutate()}
                data-testid={`guardar-${regla.clave}-${ficha.id}`}
              >
                <Check size={16} /> {guardar.isPending ? "Guardando…" : "Guardar"}
              </Boton>
            </>
          )}
        </div>
      </Td>
    </Fila>
  )
}
